import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Course } from '../../../shared/models/course';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from '../../../shared/models/item.model';
import { environment } from 'src/environments/environment';
import { CourseTypeEnum } from 'src/app/course/enums';
import { LearningPathModel } from 'src/app/learning-path/models/learning-path.model';
import { Router } from '@angular/router';
import { PaymentService } from 'src/app/payment/services/payment.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { IdentityModel } from 'src/app/@core/models/identity.model';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { FavouriteService } from '../../services/favourite.service';
import { CourseCardVariant, registrationTagStatus, resolveCourseCardVariant } from './course-card-variant';
import { ucStatusTag } from 'src/app/@core/design/status-tag';

/**
 * R7 — one card contract. Every `app-course-item` usage names its context; the context fixes which facts the card
 * shows, and each fact is rendered by one shared template whatever the design (same type chip classes, one date
 * format, one price block, one favourite button). Custom mode draws the context with its existing design number
 * (2 Detailed, 3 Basic, 4 Flip, `path` = the learning-path card); Unified Code mode always draws the single Unified
 * card (criterion 28) and only takes the contextual fact `progress` from this table.
 *
 * | context        | Custom design | facts                                                                            |
 * |----------------|---------------|----------------------------------------------------------------------------------|
 * | home-live      | 4 Flip        | image, favourite, title, description, startDate, rating, instructor, price, cta   |
 * | home-recorded  | 2 Detailed    | image, favourite, title, description, hours, rating, instructor, price, cta       |
 * | catalogue      | 3 Basic       | image, favourite, typeChip, title, price                                          |
 * | my-space       | 3 Basic       | image, favourite, typeChip, title, progress                                       |
 * | related        | 4 Flip        | image, favourite, typeChip, title, description, rating, instructor, price, cta    |
 * | profile        | 4 Flip        | image, favourite, typeChip, title, description, rating, instructor, price, cta    |
 * | path           | path card     | image, title, description, courseCount, startDate, registerEnd, cta               |
 *
 * Price = the one price block, drawn when payment is enabled and the course has a price; its cart button also needs
 * the caller's cart state (`inCart` not null). Dates use one format (`CARD_DATE_FORMAT`). The former Custom design 1
 * had no caller and was removed; its classes still style the path card.
 */
export type CourseCardContext = 'home-live' | 'home-recorded' | 'catalogue' | 'my-space' | 'related' | 'profile' | 'path';
export type CourseCardFact =
  | 'image' | 'favourite' | 'typeChip' | 'title' | 'description' | 'instructor' | 'hours' | 'startDate'
  | 'registerEnd' | 'courseCount' | 'rating' | 'price' | 'progress' | 'cta';
export type CourseCardDesign = 2 | 3 | 4 | 'path';

export interface CourseCardContract {
  design: CourseCardDesign;
  facts: readonly CourseCardFact[];
}

const BROWSE_FACTS: readonly CourseCardFact[] = ['image', 'favourite', 'typeChip', 'title', 'description', 'rating', 'instructor', 'price', 'cta'];

export const COURSE_CARD_CONTRACT: Readonly<Record<CourseCardContext, CourseCardContract>> = {
  'home-live': { design: 4, facts: ['image', 'favourite', 'title', 'description', 'startDate', 'rating', 'instructor', 'price', 'cta'] },
  'home-recorded': { design: 2, facts: ['image', 'favourite', 'title', 'description', 'hours', 'rating', 'instructor', 'price', 'cta'] },
  'catalogue': { design: 3, facts: ['image', 'favourite', 'typeChip', 'title', 'price'] },
  'my-space': { design: 3, facts: ['image', 'favourite', 'typeChip', 'title', 'progress'] },
  'related': { design: 4, facts: BROWSE_FACTS },
  'profile': { design: 4, facts: BROWSE_FACTS },
  'path': { design: 'path', facts: ['image', 'title', 'description', 'courseCount', 'startDate', 'registerEnd', 'cta'] },
};

/** Facts each Custom layout can draw; a context may only ask its design for these (checked by the spec). */
export const CUSTOM_DESIGN_FACTS: Readonly<Record<CourseCardDesign, readonly CourseCardFact[]>> = {
  2: ['image', 'favourite', 'title', 'description', 'hours', 'startDate', 'rating', 'instructor', 'price', 'progress', 'cta'],
  3: ['image', 'favourite', 'typeChip', 'title', 'price', 'progress'],
  4: ['image', 'favourite', 'typeChip', 'title', 'description', 'hours', 'startDate', 'rating', 'instructor', 'price', 'progress', 'cta'],
  'path': ['image', 'title', 'description', 'courseCount', 'startDate', 'registerEnd', 'cta'],
};

/** Facts of the single Unified course card; `progress` is added only where the context asks for it. */
export const UNIFIED_COURSE_FACTS: readonly CourseCardFact[] = ['image', 'favourite', 'typeChip', 'title', 'instructor', 'hours', 'startDate', 'rating', 'price', 'cta'];

/** One date format for every card date (Custom and Unified). */
export const CARD_DATE_FORMAT = 'mediumDate';

/**
 * Context of a caller that still passes the legacy `cardDesign` number (the catalogue passes `[cardDesign]="3"`):
 * `myCourses` means My space, 3 the catalogue, 2 the recorded-courses home section, anything else the related list.
 */
export function legacyCardContext(cardDesign: number | null | undefined, myCourses: boolean): CourseCardContext {
  if (myCourses) return 'my-space';
  if (cardDesign === 3) return 'catalogue';
  if (cardDesign === 2) return 'home-recorded';
  return 'related';
}

/** Whether a fact is drawn for a context in the given mode. */
export function cardShowsFact(context: CourseCardContext, fact: CourseCardFact, unified: boolean): boolean {
  const contextFacts = COURSE_CARD_CONTRACT[context].facts;
  if (unified && context !== 'path') {
    return fact === 'progress' ? contextFacts.includes('progress') : UNIFIED_COURSE_FACTS.includes(fact);
  }
  return contextFacts.includes(fact);
}

@Component({
  selector: 'app-course-item',
  templateUrl: './course-item.component.html',
  styleUrls: ['./course-item.component.scss']
})
export class CourseItemComponent implements OnInit, OnDestroy {
  isRtl: boolean = true;
  @Input() course: Course;
  favorite: boolean = false;
  /** Legacy design number; used only to infer the context when `context` is not given (see `legacyCardContext`). */
  @Input() cardDesign: number | null = null;
  /** R7: the usage context; it selects the facts and the Custom design (see COURSE_CARD_CONTRACT). */
  @Input() context?: CourseCardContext;
  @Input() path: LearningPathModel;
  dateStatus = { name:'', class:'' };
  @Input() myCourses: boolean = false;
  resource = environment.courseResource;
  @Input() showRegisterStatus: boolean = true;
  private destroySubject: Subject<void> = new Subject();
  @Input() inCart: boolean | null;
  identityModel: IdentityModel = {} as IdentityModel;

  constructor(private langService: LanguageService, 
    private paymentService: PaymentService,
    private authService: AuthService,
    private identityService: IdentityService,
    private favouriteService: FavouriteService,
    private _router: Router){}

  ngOnInit(): void {
    this.isRtl = this.langService.getCurrentLanguage() === LanguageEnum.ar;
    this.langService.languageChange
    .pipe(takeUntil(this.destroySubject))
    .subscribe((result: LanguageEnum) => this.isRtl = result === LanguageEnum.ar)
    if(this._router.url.includes('myspace')) this.showRegisterStatus = false;
    if(this.course) {
      this.getDateStatus();
      this.favorite = !!this.course.isFavourite;
    }
    this.identityModel = this.identityService.getIdentityModel();
  }
  get CourseType(){
    return CourseTypeEnum;
  }
  readonly dateFormat = CARD_DATE_FORMAT;

  get cardContext(): CourseCardContext {
    if (this.path && !this.course) return 'path';
    return this.context ?? legacyCardContext(this.cardDesign, this.myCourses);
  }
  get contract(): CourseCardContract {
    return COURSE_CARD_CONTRACT[this.cardContext];
  }
  /** Unified Code (criterion 28): one card for every usage; Custom mode draws the context's design. */
  get variant(): CourseCardVariant {
    const design = this.contract.design;
    return resolveCourseCardVariant(this.identityService.isUnifiedCode(), design === 'path' ? null : design);
  }
  shows(fact: CourseCardFact): boolean {
    return cardShowsFact(this.cardContext, fact, this.identityService.isUnifiedCode());
  }
  /** The one price block: payment enabled and a priced course (the cart button inside also needs `inCart`). */
  get showsPrice(): boolean {
    return this.shows('price') && !!this.identityModel?.enablePayment && !!this.course?.price;
  }
  get courseTitle(): string {
    if (!this.course) return '';
    return (this.isRtl ? this.course.titleAr || this.course.title : this.course.title || this.course.titleAr) ?? '';
  }
  get courseDescription(): string {
    return this.course?.description || this.course?.definition || '';
  }
  /** Type-chip colour class, the same in every Custom design. */
  get courseTypeClass(): string | null {
    switch (this.course?.type) {
      case CourseTypeEnum.Recorded: return 'recorded';
      case CourseTypeEnum.LiveSessions: return 'live';
      case CourseTypeEnum.OnSite: return 'onsite';
      case CourseTypeEnum.Compact: return 'compact';
      default: return null;
    }
  }
  get courseTypeKey(): string | null {
    switch (this.course?.type) {
      case CourseTypeEnum.Recorded: return 'course.types.recorded';
      case CourseTypeEnum.LiveSessions: return 'course.types.live';
      case CourseTypeEnum.OnSite: return 'course.types.onsite';
      case CourseTypeEnum.Compact: return 'course.types.compact';
      default: return null;
    }
  }
  get instructorNames(): string {
    return (this.course?.instructors ?? [])
      .map(instructor => this.isRtl ? instructor.instructorNameAr || instructor.instructorName : instructor.instructorName || instructor.instructorNameAr)
      .filter(name => !!name)
      .join(this.isRtl ? '، ' : ', ');
  }
  get discountedPrice(): number {
    if (!this.course?.price) return 0;
    return this.course.price - (this.course.price * (this.course.discount || 0)) / 100;
  }
  /** Registration status tag classes (criterion 26: status colours via the shared ucStatusTag()). */
  get registrationTagClass(): string {
    return ucStatusTag(registrationTagStatus(this.dateStatus.name));
  }
  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }
  // E2E-143: the heart used to flip a local flag only, so a favourite was lost on the next render.
  // It now toggles `StudentFavourites` through the trainee favourites endpoint (owner = token user).
  toggleFavourite(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.authService.isAuthenticated() || !this.course) return;
    const previous = this.favorite;
    this.favorite = !previous;
    this.favouriteService
      .updatStatus(this.course.id)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => { this.course.isFavourite = this.favorite; },
        error: () => { this.favorite = previous; },
      });
  }
  getDateStatus(): void {
    if(this.course && this.course.registerStart && this.course.registerEnd) {
      var currentDate = new Date();
      var regStart= new Date(this.course.registerStart );
      var regEnd= new Date(this.course.registerEnd );
      if(regStart <= currentDate && regEnd >= currentDate && this.course.requireApprovement == true) {
        this.dateStatus.name = "registerNow";
        this.dateStatus.class = 'badge-success';
      } else if(regStart > currentDate) {
        this.dateStatus.name = "registerSoon";
        this.dateStatus.class = 'badge-soon';
      } else if(regEnd < currentDate) {
        this.dateStatus.name = "registerEnd";
        this.dateStatus.class = 'badge-closed';
      }
      else if(this.course.requireApprovement == true && regEnd >= currentDate){
        this.dateStatus.name = "registerNow";
        this.dateStatus.class = 'badge-success';
      }
      else if(this.course.requireApprovement == false && regEnd >= currentDate){
        this.dateStatus.name = "registerSoon";
        this.dateStatus.class = 'badge-soon';
      }
    }
    else if(this.course.requireApprovement == true && regEnd >= currentDate){
      this.dateStatus.name = "registerNow";
      this.dateStatus.class = 'badge-success';
    }
    else if(this.course && ((this.course.registerStart === null || this.course.registerEnd === null) ||
      (this.course.registerStart !== null && new Date(this.course.registerStart) < new Date()))) {
        this.dateStatus.name = "registerNow";
        this.dateStatus.class = 'badge-success';
    }
  }

  addToCart() {
    if (this.inCart) this._router.navigate(['/check-out']);
    else if (!this.authService.isAuthenticated()) {
      var cartItems = [];
      var items = JSON.parse(localStorage.getItem('cartItems'));
      if (items) cartItems = items;
      cartItems.push({courseId: this.course.id});
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      this.inCart = true;
      this.paymentService.itemsInCart += 1;
    }
    else {
      this.paymentService.addToCart({courseId: this.course.id})
      .subscribe(()=>{
        this.inCart = true;
        this.paymentService.itemsInCart += 1;
      }, () => {});
    }
  }
  
  ngOnDestroy(): void {
    this.destroySubject.next();
  }
}
